import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack, Tabs, Tab
} from '@mui/material';
import { useSnackbar } from 'notistack';
import ubicacionService, { Ubicacion } from '../../services/ubicacionService';

const defaultForm: Ubicacion = {
  nroUbicacion: 0,
  nombreUbicacion: '',
  nroSuc: 0,
  direccion: '',
  telefono: '',
  responsable: ''
};

const UbicacionForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { nroUbicacion } = useParams();
  const [formData, setFormData] = useState<Ubicacion>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  const loadUbicacion = useCallback(async () => {
    if (!nroUbicacion || nroUbicacion === 'nuevo') return;
    try {
      setLoading(true);
      const ubicacion = await ubicacionService.getUbicacion(Number(nroUbicacion));
      setFormData(ubicacion);
    } catch (error) {
      enqueueSnackbar('Error al cargar la ubicación', { variant: 'error' });
      navigate('/configuraciones/ubicaciones');
    } finally {
      setLoading(false);
    }
  }, [nroUbicacion, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (nroUbicacion && nroUbicacion !== 'nuevo') loadUbicacion();
  }, [nroUbicacion, loadUbicacion]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'nroUbicacion' || name === 'nroSuc') {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (!formData.nombreUbicacion.trim()) {
        enqueueSnackbar('El nombre de la ubicación es requerido', { variant: 'warning' });
        return;
      }

      if (nroUbicacion && nroUbicacion !== 'nuevo') {
        await ubicacionService.updateUbicacion(Number(nroUbicacion), formData);
      } else {
        await ubicacionService.createUbicacion(formData);
      }

      enqueueSnackbar('Ubicación guardada exitosamente', { variant: 'success' });
      navigate('/configuraciones/ubicaciones');
    } catch (error: any) {
      console.error('Error al guardar ubicación:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar la ubicación', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {nroUbicacion && nroUbicacion !== 'nuevo' ? 'Editar Ubicación' : 'Nueva Ubicación'}
          </Typography>

          <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 3 }}>
            <Tab label="General" />
            <Tab label="Configuración" />
          </Tabs>

          <form onSubmit={handleSubmit}>
            {currentTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <TextField 
                    name="nroUbicacion" 
                    label="N° Ubicación" 
                    type="number"
                    value={formData.nroUbicacion} 
                    onChange={handleChange} 
                    fullWidth 
                    required 
                    disabled={!!(nroUbicacion && nroUbicacion !== 'nuevo')}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={9}>
                  <TextField 
                    name="nombreUbicacion" 
                    label="Nombre" 
                    value={formData.nombreUbicacion} 
                    onChange={handleChange} 
                    fullWidth 
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    name="direccion" 
                    label="Dirección" 
                    value={formData.direccion || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="telefono" 
                    label="Teléfono" 
                    value={formData.telefono || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="responsable" 
                    label="Responsable" 
                    value={formData.responsable || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="nroSuc" 
                    label="N° Sucursal" 
                    type="number"
                    value={formData.nroSuc || 0} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/configuraciones/ubicaciones')} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UbicacionForm;
